/** Maps table row id → API `server_category`. */
export const APP_CATEGORY_BY_ID = {
  notification: 'notification',
  customer: 'customer',
  kitchen: 'kitchen',
  'regional-branch': 'regionalbranch',
  driver: 'driver',
};

/** Maps table column key → API `environment` values. */
const ENV_VALUES_BY_COLUMN = {
  production: ['production', 'prod'],
  uat: ['uat'],
  develop: ['development', 'develop', 'dev'],
  test: ['test'],
};

function normalizeEnv(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function mapEnvironmentToColumnKey(environment) {
  const env = normalizeEnv(environment);
  if (!env) return null;
  for (const [columnKey, values] of Object.entries(ENV_VALUES_BY_COLUMN)) {
    if (values.includes(env)) return columnKey;
  }
  return null;
}

function heartbeatMs(record) {
  const raw = record?.last_heartbeat_at ?? record?.update_at ?? record?.last_status_change_at;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * @param {object[]} flags
 * @returns {Map<string, object>} keys `${appId}:${envColumnKey}`
 */
export function buildServerFlagsLookup(flags) {
  const map = new Map();
  if (!Array.isArray(flags)) return map;

  for (const flag of flags) {
    const category = normalizeEnv(flag?.server_category);
    const envKey = mapEnvironmentToColumnKey(flag?.environment);
    if (!category || !envKey) continue;

    const appId = Object.entries(APP_CATEGORY_BY_ID).find(([, cat]) => cat === category)?.[0];
    if (!appId) continue;

    const lookupKey = `${appId}:${envKey}`;
    const existing = map.get(lookupKey);
    if (!existing || heartbeatMs(flag) >= heartbeatMs(existing)) {
      map.set(lookupKey, flag);
    }
  }

  return map;
}

export function getServerFlagForCell(lookup, appId, envColumnKey) {
  if (!lookup) return null;
  return lookup.get(`${appId}:${envColumnKey}`) ?? null;
}

export function isServerUp(record) {
  return normalizeEnv(record?.current_status) === 'up';
}

/**
 * @param {object[]} flags
 * @returns {number|null}
 */
export function getLatestFlagsTimestamp(flags) {
  if (!Array.isArray(flags) || flags.length === 0) return null;
  let max = 0;
  for (const flag of flags) {
    const ms = heartbeatMs(flag);
    if (ms > max) max = ms;
  }
  return max > 0 ? max : null;
}

export function formatLastUpdated(timestamp) {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} - ${timePart}`;
}

export function formatLogTimestamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}
